import React from 'react';
import { CommonVendorsTable } from './CommonVendorsTable';
import { getColumnDefs, PAGE_SIZE } from './utils';
import isEqual from 'lodash/isEqual';
import ChangeTypeModal from 'Details/ChangeTypeModal/ChangeTypeModal';
import MoveToBinModal from 'Details/MoveToBinModal/MoveToBinModal';
import CategorisationDialog from 'Explore/CategorisationDialog';
import { SPEND_TYPE } from 'Data/constants';
import { getCommonVendorsData, changeCategorisation } from 'utils/apiRequests';
import { TAIL_SPEND } from 'utils/detailsChartDataConverter';
import { PmiContext } from '../../context';

export class CommonVendorsTableContainer extends React.Component {
	state = {
		data: [],
		totalCount: 0,
		isLoading: false,
		headers: [],
		page: 1,
		sortModel: null,
		categorisationModalData: null,
		changeStatusModalData: null,
		moveToBinModalData: null,
	};

	static contextType = PmiContext;

	componentDidMount() {
		this.fetchData();
	}

	componentDidUpdate(prevProps) {
		const { queryParams } = this.props;

		if (!isEqual(queryParams, prevProps.queryParams)) {
			this.setState({ page: 1, sortModel: null }, this.fetchData);
		}
	}

	getSortParams = () => {
		const { headers, sortModel } = this.state;

		if (!sortModel) {
			return {};
		}

		const column = headers.find(item => item.colName === sortModel.colId);

		return {
			sortDirection: sortModel && sortModel.sort,
			sortIndex: column && column.sortIndex,
		};
	};

	getApiParams = () => {
		const { queryParams } = this.props;
		const { page } = this.state;

		return {
			...queryParams,
			...this.getSortParams(),
			take: PAGE_SIZE,
			skip: PAGE_SIZE * (page - 1),
		};
	};

	fetchData = () => {
		const { page, data } = this.state;

		this.setState({ isLoading: true });

		return getCommonVendorsData(this.getApiParams())
			.then(({ headers, body, totalCount }) => {
				this.setState({
					data: page === 1 ? body : [...data, ...body],
					headers,
					totalCount,
					isLoading: false,
				});
			})
			.catch(() => {
				this.setState({ isLoading: false });
			});
	};

	getColumnDefs = () => {
		const { companies, spendFormatter } = this.props;
		const { sortModel, totalCount } = this.state;

		return getColumnDefs(companies, spendFormatter, sortModel, totalCount);
	};

	handleLoadMore = () => {
		const { page } = this.state;

		this.setState({ page: page + 1 }, this.fetchData);
	};

	handleSortChanged = ([sortModel]) => {
		if (sortModel) {
			this.setState({ sortModel, page: 1 }, this.fetchData);
		}
	};

	handleViewTransactionsClick = row => {
		const { pmiBreadcrumbs, showExplorePage } = this.props;
		const options = { vendorName: row.vendorName };
		const exploreHierarchy = pmiBreadcrumbs.filter(item => item !== TAIL_SPEND);

		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;
		if (spendType) {
			options.spendType = spendType.toUpperCase();
		}

		if (exploreHierarchy && exploreHierarchy.length > 0) {
			options.hierarchy = exploreHierarchy;
		}

		showExplorePage(options, row.name);
	};

	getVendorTransactionsParams = () => {
		const { categorisationModalData } = this.state;

		if (!categorisationModalData) {
			return {};
		}

		return {
			filters: ['harmonizedSupplier'],
			filterValues: [categorisationModalData.vendorName],
		};
	};

	getContextParams = () => {
		const { categorisationModalData } = this.state;

		if (!categorisationModalData) {
			return {};
		}

		const apiParams = this.getApiParams();
		const apiParamsContext = {
			dateFrom: apiParams.dateFrom,
			dateTo: apiParams.dateTo,
			hierarchy: apiParams.hierarchy,
			filters: apiParams.filters,
			filterValues: apiParams.filterValues,
		};

		return {
			...apiParamsContext,
			filters: [...apiParamsContext.filters, 'harmonizedSupplier'],
			filterValues: [
				...apiParamsContext.filterValues,
				categorisationModalData.vendorName,
			],
		};
	};

	handleChangeCategorisation = ({ taxonomy, isDirect, isAllTransactions }) => {
		const params = isAllTransactions
			? this.getVendorTransactionsParams()
			: this.getContextParams();
		const spendType = isDirect ? SPEND_TYPE.DIRECT : SPEND_TYPE.INDIRECT;
		const data = !isDirect
			? {
					l4Code: taxonomy.aP_L4Code,
			  }
			: {
					l1Category: taxonomy.project_L1Category,
					l2Category: taxonomy.project_L2Category,
					l3Category: taxonomy.project_L3Category,
					l4Category: taxonomy.project_L4Category,
			  };

		return changeCategorisation(spendType, params, data).then(() => {
			const { refreshPmiContent } = this.context;
			if (refreshPmiContent) {
				refreshPmiContent();
			}
		});
	};

	handleChangeStatus = row => {
		const { pmiBreadcrumbs, companies } = this.props;
		const exploreHierarchy = pmiBreadcrumbs.filter(item => item !== TAIL_SPEND);

		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;
		const data = {
			selectedDimensionKey: 'harmonizedSupplier',
			selectedDimensionName: row.vendorName,
			selectedDimensionKeyTitle: 'vendor',
			...(exploreHierarchy.length > 0 ? { hierarchy: exploreHierarchy } : {}),
			extraFilters: {
				company: companies,
			},
		};

		if (spendType) {
			data.spendType = spendType.toUpperCase();
		}

		this.setState({
			changeStatusModalData: data,
		});
	};

	handleMoveToBin = row => {
		const { pmiBreadcrumbs, companies } = this.props;
		const exploreHierarchy = pmiBreadcrumbs.filter(item => item !== TAIL_SPEND);

		const spendType =
			exploreHierarchy && exploreHierarchy.length > 0
				? exploreHierarchy.shift()
				: null;
		const data = {
			selectedDimensionKey: 'harmonizedSupplier',
			selectedDimensionName: row.vendorName,
			selectedDimensionKeyTitle: 'vendor',
			...(exploreHierarchy.length > 0 ? { hierarchy: exploreHierarchy } : {}),
			extraFilters: {
				company: companies,
			},
		};

		if (spendType) {
			data.spendType = spendType.toUpperCase();
		}

		this.setState({
			moveToBinModalData: data,
		});
	};

	showCategorisationModal = categorisationModalData => {
		this.setState({ categorisationModalData });
	};

	hideCategorisationModal = () => {
		this.setState({ categorisationModalData: null });
	};

	render() {
		const {
			isLoading,
			data,
			categorisationModalData,
			changeStatusModalData,
			moveToBinModalData,
			totalCount,
		} = this.state;
		const { refreshPmiContent } = this.context;

		return (
			<React.Fragment>
				<CommonVendorsTable
					isLoading={isLoading}
					data={data}
					totalCount={totalCount}
					loadMore={this.handleLoadMore}
					onSortChanged={this.handleSortChanged}
					columnDefs={this.getColumnDefs()}
					showCategorisationModal={this.showCategorisationModal}
					onViewTransactionsClick={this.handleViewTransactionsClick}
					onChangeStatus={this.handleChangeStatus}
					onMoveToBin={this.handleMoveToBin}
				/>
				{categorisationModalData && (
					<CategorisationDialog
						name={categorisationModalData.vendorName}
						onClose={this.hideCategorisationModal}
						onSave={this.handleChangeCategorisation}
						contextParams={this.getContextParams()}
						allTransactionsParams={this.getVendorTransactionsParams()}
					/>
				)}
				{changeStatusModalData && (
					<ChangeTypeModal
						isOpen={changeStatusModalData}
						isHarmonized
						{...changeStatusModalData}
						onClose={() => {
							this.setState({ changeStatusModalData: null });
						}}
						onSubmit={refreshPmiContent}
						refreshPmiContent={refreshPmiContent}
						isPmi
					/>
				)}
				{moveToBinModalData && (
					<MoveToBinModal
						isOpen={moveToBinModalData}
						isHarmonized
						{...moveToBinModalData}
						onClose={() => {
							this.setState({ moveToBinModalData: null });
						}}
						onSubmit={refreshPmiContent}
						refreshPmiContent={refreshPmiContent}
						isPmi
					/>
				)}
			</React.Fragment>
		);
	}
}

export default CommonVendorsTableContainer;
